import React from "react";
import PropTypes from 'prop-types';
import {EmptyState} from '@communityshare/ui-shared'
import {DocumentMagnifyingGlassIcon as EmptyIcon} from '@heroicons/react/24/outline'
import DonutChart from "@/pages/dashboard/_charts/DonutChart";
import colors from "tailwindcss/colors";
import Card from "@/components/Card";
import ChartHeader from "@/pages/dashboard/_charts/ChartHeader";
import Loader from "@/components/Loader";
import ErrorMessage from "@/forms/_common/ErrorMessage";
import useCoreEvaluationQuestions from "@/features/dashboard-eval/hooks/useCoreEvaluationQuestions";
import {useTranslation} from "react-i18next";

const YES_NO_OPTIONS = [
  `Yes`,
  `No`
]

EvalResponses.propTypes = {
  network: PropTypes.string,
  schoolStart: PropTypes.string,
  schoolEnd: PropTypes.string,
  table: PropTypes.string.isRequired,
  ratingOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  titleOnly: PropTypes.bool,
  showTotals: PropTypes.bool
}

function resolveChartData(question, ratingOptions) {
  const firstResponse = question.responses.at(0)?.response;
  const labels = ratingOptions.includes(firstResponse)
    ? ratingOptions
    : YES_NO_OPTIONS.includes(firstResponse) ? YES_NO_OPTIONS : [];
  const series = labels.map((label) => question.responses.find((r) => r.response === label)?.count || 0);
  return {labels, series};
}

export default function EvalResponses({network, schoolStart: startDate, schoolEnd: endDate, table, ratingOptions}) {
  const {
    data,
    isLoading,
    isError,
    isRefetching,
    refetch
  } = useCoreEvaluationQuestions({
    networkId: network?.id,
    startDate,
    endDate
  })
  const {t} = useTranslation();
  let subtitleKey = `dashboard.evaluations.subtitle.${table}`
  if (startDate || endDate) subtitleKey += 'Year'

  if (isLoading || isRefetching) return (
    <Card className="pt-2 pb-4 px-4 relative space-y-6 min-h-[200px]">
      <ChartHeader
        title={t(`dashboard.evaluations.title.${table}`)}
        subtitle={t(subtitleKey)}
      />
      <Loader/>
    </Card>
  )
  if (isError) return (
    <Card className="pt-2 pb-4 px-4 relative space-y-6 min-h-[200px]">
      <ChartHeader
        title={t(`dashboard.evaluations.title.${table}`)}
        subtitle={t(subtitleKey)}
      />
      <ErrorMessage>We had some trouble getting the data</ErrorMessage>
      <button className="btn btn-primary min-h-12 md:min-h-10 hover:bg-cs-orange-400"
              onClick={() => refetch()}>
        Try again
      </button>
    </Card>
  )

  // No data
  if (!data[table]) return (
    <Card className="pt-2 pb-4 px-4 relative space-y-6 min-h-[200px]">
      <ChartHeader
        title={t(`dashboard.evaluations.title.${table}`)}
        subtitle={t(subtitleKey)}
      />
      <EmptyState title={t('dashboard.evaluations.no_responses')}
                  description={t('dashboard.evaluations.no_responses_subtitle')}
                  icon={EmptyIcon}></EmptyState>
    </Card>
  )

  return (
    <>
      {data[table].map((question) => {
        const {labels, series} = resolveChartData(question, ratingOptions);
        return (
          <DonutChart
            key={`${question.question}`}
            title={t(`dashboard.evaluations.questions.${question.question}`)}
            subtitle={t(subtitleKey)}
            labels={labels}
            series={series}
            colors={[
              colors.emerald['400'],
              colors.yellow['400'],
              colors.red['400'],
              colors.blue['400'],
              colors.purple['400']
            ]}
          />
        );
      })}
    </>
  );
}
